import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Store, Select, Actions, ofActionCompleted, ActionCompletion } from '@ngxs/store';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { AuthState } from '../core/auth/auth.state';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { StateMessage } from '../core/state.types';
import { Login, Logout, ResetPassword } from '@oneapp/core/auth/auth.actions';
import { ResetPasswordParamsAPI } from '@oneapp/core/auth/auth.models';
import { OaError } from '@oneapp/core/http-error.interceptor';
import { ForgotPasswordRouterState } from '@oneapp/forgot-password/forgot-password.models';
import { AppRoute } from '@oneapp/app-routing.models';
import { ResetPasswordDataCySelector } from './reset-password.models';

@Component({
    selector: 'oa-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    @Select(AuthState.authStatusMessage) public authStatusMessage$: Observable<StateMessage>;
    @Select(AuthState.isCreatingNewPassword) public isCreatingNewPassword$: Observable<boolean>;

    public resetPasswordForm = new UntypedFormGroup({
        password: new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
        confirmPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(8), this._passwordsMatchValidator])
    });

    public submitDisabled$: Observable<boolean>;

    public dataCySelectors = ResetPasswordDataCySelector;

    private _destroy$: Subject<boolean> = new Subject();

    constructor(
        private _store: Store,
        private _route: ActivatedRoute,
        private _actions: Actions,
        private _router: Router
    ) { }

    public ngOnInit() {
        this._store.select(AuthState.isLoggedIn).pipe(
            filter(isLoggedIn => isLoggedIn),
            takeUntil(this._destroy$)
        ).subscribe(() => {
            // logout the user if they are already logged in
            // so if they are trying to set a password for a new user while also authenticated as another user
            // they will be logged in as the new user when they finish
            this._store.dispatch(new Logout(false));
        });


        this.submitDisabled$ = this.resetPasswordForm.statusChanges.pipe(
            map(changes => changes === 'VALID' ? false : true),
            startWith(true)
        );
    }

    public ngOnDestroy(): void {
        this._destroy$.next(true);
        this._destroy$.complete();
    }

    public onSubmit() {
        const uniquetoken = this._route.snapshot.params.uniqueToken;
        const password = this.resetPasswordForm.controls.password.value;
        // unique token has the form <CODE>||<EMAIL>
        const username = uniquetoken.split('||')[1];
        const code = uniquetoken.split('||')[0];
        const payload: ResetPasswordParamsAPI = {
            username,
            password,
            code
        };

        this._store.dispatch(new ResetPassword(payload)).pipe(
            take(1)
        ).subscribe(() => {
            this._store.dispatch(new Login({ username, password }));
        });

        // pipe that handles the error when the unique token has expired
        this._actions.pipe(
            ofActionCompleted(ResetPassword),
            map((a: ActionCompletion<ResetPassword>) => {
                return a.result.error as OaError;
            }),
            // 410 is the error BE gives when the token has expired
            filter(error => !!error && error.status === 410),
            takeUntil(this._destroy$)
        ).subscribe((error) => {
            const forgotPasswordRouterState: ForgotPasswordRouterState = {
                message: error.message,
                email: username
            };

            this._router.navigate(
                [AppRoute.FORGOT_PASSWORD],
                {
                    state: forgotPasswordRouterState
                }
            );
        });
    }

    private _passwordsMatchValidator(control: UntypedFormGroup): ValidationErrors | null {
        const parent = control?.parent;
        if (!parent) {
            return null;
        }

        const passwordKey = 'password';
        const confirmPasswordKey = 'confirmPassword';

        const password = parent.controls[passwordKey];
        const confirmPassword = parent.controls[confirmPasswordKey];

        const passwordNotAMatch = {
            validate: (passwordToValidate: AbstractControl, confirmPasswordToValidate: AbstractControl) => passwordToValidate
                    && confirmPasswordToValidate
                    && passwordToValidate.value !== confirmPasswordToValidate.value
                    && confirmPasswordToValidate.value.length > 0
        };

        return passwordNotAMatch.validate(password, confirmPassword)
            ? { 'passwordsDontMatch': true }
            : {};
    }
}
